import React from 'react';
import moment from 'moment';
import {
  Bell,
  Building,
  Check2Circle,
  Envelope,
  EnvelopeArrowUp,
  EnvelopeOpen,
  FileEarmarkText,
  Link45deg,
  PersonX,
  Plus,
  Telephone,
} from 'react-bootstrap-icons';

export type TimelineItem = {
  id: string;
  name: string;
  valueOld: string;
  valueNew: string;
  description: string;
};

export type TimelineContextNode = {
  id: string;
  name: string;
  refId?: string;
  typeName?: string;
  level?: string;
};

export type TimelineChange = {
  id: string;
  created: string;
  createdByName: string;
  description: string;
  changeType: string;
  profileImage: string;
  title: string;
  items: TimelineItem[];
  objectName?: string;
  status?: string;
  statusColor?: string;
  contextPath?: TimelineContextNode[];
};

export type FilterFormData = {
  userId: string;
  periodFrom: any;
  periodTo: any;
  entryType: Set<any>;
  marketingType: Set<any>;
  contextType: Set<any>;
};

export type TimelineVariant = {
  key: string;
  icon: React.ReactNode;
  iconClassName: string;
  accentClassName?: string;
  showReloadBadge?: boolean;
  badgeIcon?: React.ReactNode;
  badgeClassName?: string;
  chipTone?: 'neutral' | 'status';
  supportsExpand?: boolean;
};

export const PAGE_SIZE = 30;

export const ENTRY_TYPE_OPTIONS = [
  { id: 'EMAIL', name: 'Email' },
  { id: 'NOTICE', name: 'Notice' },
  { id: 'MEETING', name: 'Meeting' },
  { id: 'MASSEMAIL', name: 'Massmail' },
  { id: 'MAILMERGE', name: 'Mailmerge' },
  { id: 'DOCUMENT', name: 'Document' },
  { id: 'WORKFLOW', name: 'Workflow' },
  { id: 'PHONECALL', name: 'Phonecall' },
  { id: 'LETTER', name: 'Letter' },
  { id: 'SMS', name: 'SMS' },
  { id: 'FORM', name: 'Form' },
  { id: 'ACTIVITY', name: 'Activity' },
  { id: 'PROJECT', name: 'Project' },
  { id: 'FOLDER', name: 'Folder' },
  { id: 'MESSAGE', name: 'Message' },
];

export const MARKETING_TYPE_OPTIONS = [
  { id: 'GOAL-ACHIEVED', name: 'Goal achieved' },
  { id: 'UNSUBSCRIBE', name: 'Unsubscribe' },
  { id: 'MAIL-OPEN', name: 'Mail open' },
  { id: 'URL-VISIT', name: 'URL visit' },
  { id: 'MAIL-URL-VISIT', name: 'Mail URL visit' },
  { id: 'NEW-PHONE-NUMBER', name: 'New phone number' },
];

export const normalizeTimelineItems = (itemsValue: any): TimelineItem[] => {
  const items = Array.isArray(itemsValue?.ITEM) ? itemsValue.ITEM : itemsValue?.ITEM ? [itemsValue.ITEM] : [];
  return items.map((item: any) => ({
    id: String(item?.id || ''),
    name: String(item?.name || ''),
    valueOld: String(item?.valueOld || ''),
    valueNew: String(item?.valueNew || ''),
    description: String(item?.description || ''),
  }));
};

export const normalizeTimelineChanges = (changeValue: any): TimelineChange[] => {
  const changes = Array.isArray(changeValue) ? changeValue : changeValue ? [changeValue] : [];
  return changes.map((change: any) => ({
    id: String(change?.id || ''),
    created: String(change?.created || ''),
    createdByName: String(change?.createdByName || ''),
    description: String(change?.description || ''),
    changeType: String(change?.changeType || ''),
    profileImage: String(change?.profileImage || ''),
    title: String(change?.description || change?.changeType || 'Timeline entry'),
    items: normalizeTimelineItems(change?.ITEMS),
    objectName: change?.objectName ? String(change.objectName) : undefined,
    contextPath: (Array.isArray(change?.CONTEXT_PATH?.CONTEXT) ? change.CONTEXT_PATH.CONTEXT : change?.CONTEXT_PATH?.CONTEXT ? [change.CONTEXT_PATH.CONTEXT] : []).map((node: any) => ({
      id: String(node?.id || ''),
      name: String(node?.name || ''),
      refId: node?.refId ? String(node.refId) : undefined,
      typeName: node?.typeName ? String(node.typeName) : undefined,
      level: node?.level ? String(node.level) : undefined,
    })),
  }));
};

export const groupByCreatedMonth = (data: TimelineChange[]): Record<string, TimelineChange[]> => {
  return data.reduce((acc: Record<string, TimelineChange[]>, item) => {
    const createdDate = new Date(item.created);
    const monthYear = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;
    if (!acc[monthYear]) acc[monthYear] = [];
    acc[monthYear].push(item);
    return acc;
  }, {});
};

export const formatMonthHeader = (value: string) => {
  const [year, month] = value.split('-');
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${monthNames[Number(month) - 1]} ${year}`;
};

export const formatEntryDate = (dateString: string, t: any) => {
  const date = moment(dateString);
  if (date.isSame(moment(), 'day')) return t('today', { ns: 'layout_components', defaultValue: 'Today' });
  if (date.isSame(moment().subtract(1, 'day'), 'day')) return t('yesterday', { ns: 'layout_components', defaultValue: 'Yesterday' });
  return date.format('D.M. YYYY | HH:mm');
};

export const formatFieldName = (value: string) => {
  const normalized = String(value || '').replace(/Value$/i, '');
  const lower = normalized.toLowerCase();
  if (lower === 'teaser') return '';
  if (lower === 'mailfrom') return 'Mail From';
  if (lower === 'mailto') return 'Mail To';
  if (lower === 'cc') return 'Cc';
  if (lower === 'bcc') return 'Bcc';
  if (lower === 'subject') return 'Subject';
  if (lower === 'recipient') return 'Recipient';
  if (lower === 'user') return 'User';
  if (lower === 'comment') return 'Comment';
  if (lower === 'customstate') return 'Custom state';
  if (lower === 'customstatename') return 'Custom state';
  if (lower === 'projecttype') return 'Project type';
  if (lower === 'projecttypename') return 'Project type';
  if (lower === 'activitytype') return 'Activity type';
  if (lower === 'activitytypename') return 'Activity type';
  if (lower === 'foldertype') return 'Folder type';
  if (lower === 'foldertypename') return 'Folder type';
  return normalized.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
};

export const truncateText = (text: string, maxLength: number) => (
  text && text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
);

const decodeHtmlEntities = (value: string) => {
  return value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/&#x27;/gi, "'");
};

const stripHtml = (value: string) => {
  if (!value) return value;
  return decodeHtmlEntities(value)
    .replace(/<!DOCTYPE[^>]*>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+\n/g, '\n')
    .replace(/\n\s+/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const formatMailboxValue = (value: string) => {
  if (!value) return value;

  const sanitized = stripHtml(value);
  const mailboxMatch = sanitized.match(/"?([^"<]+?)"?\s*<\s*([^<>@\s]+@[^<>@\s]+)\s*>/);
  if (mailboxMatch) {
    const displayName = mailboxMatch[1]?.trim().replace(/^"|"$/g, '');
    const email = mailboxMatch[2]?.trim();
    if (displayName && email) {
      return `${displayName} (${email})`;
    }
    return email || displayName || sanitized;
  }

  return sanitized;
};

export const formatRecipientValue = (value: string) => {
  if (!value) return value;

  const decoded = decodeHtmlEntities(value);

  const xmlRecipients = Array.from(decoded.matchAll(/>([^<>@\s]+@[^<>@\s]+)</g))
    .map((match) => match[1]?.trim())
    .filter(Boolean);

  if (xmlRecipients.length > 0) {
    return xmlRecipients.join(', ');
  }

  const mailboxRecipients = Array.from(
    decoded.matchAll(/"?([^"<]*?)"?\s*<\s*([^<>@\s]+@[^<>@\s]+)\s*>/g)
  )
    .map((match) => {
      const displayName = match[1]?.trim().replace(/^"|"$/g, '');
      const email = match[2]?.trim();
      return displayName && email ? `${displayName} (${email})` : email || displayName;
    })
    .filter(Boolean);

  if (mailboxRecipients.length > 0) {
    return mailboxRecipients.join(', ');
  }

  return stripHtml(decoded).replace(/\s+/g, ' ').trim();
};

const getRecipientGroups = (value: string) => {
  const decoded = decodeHtmlEntities(value || '');
  const extractGroup = (tagName: string) => Array.from(
    decoded.matchAll(new RegExp(`<${tagName}[^>]*>(.*?)</${tagName}>`, 'gi'))
  )
    .map((match) => stripHtml(match[1] || '').trim())
    .filter(Boolean);

  return {
    to: extractGroup('TO'),
    cc: extractGroup('CC'),
    bcc: extractGroup('BCC'),
    user: extractGroup('USER'),
  };
};

const getPrimaryRecipientDetail = (item: TimelineChange) => {
  const recipientItem = item.items.find((detail) => detail.name.toLowerCase() === 'recipient');
  return recipientItem ? getRecipientGroups(recipientItem.valueNew || recipientItem.valueOld || recipientItem.description || '') : null;
};

const hasStrongEmailEvidence = (item: TimelineChange) => {
  const recipientGroups = getPrimaryRecipientDetail(item);
  const hasRecipientAddress = !!recipientGroups
    && (recipientGroups.to.length > 0 || recipientGroups.cc.length > 0 || recipientGroups.bcc.length > 0);
  return !!getMailFromValue(item).trim() || hasRecipientAddress;
};

const tokenizeIdentity = (value: string) => {
  return String(value || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/g)
    .map((token) => token.trim())
    .filter((token) => token.length >= 3);
};

const getMailFromValue = (item: TimelineChange) => {
  const detail = item.items.find((entry) => entry.name.toLowerCase() === 'mailfrom');
  return detail ? String(detail.valueNew || detail.valueOld || detail.description || '') : '';
};

const getMailFromDetailItem = (item: TimelineChange) => {
  const detail = item.items.find((entry) => entry.name.toLowerCase() === 'mailfrom');
  return detail || null;
};

const isLikelySentEmail = (item: TimelineChange) => {
  const mailFromRaw = getMailFromValue(item);
  if (!mailFromRaw) return false;

  const createdByTokens = tokenizeIdentity(item.createdByName || '');
  const mailFromTokens = tokenizeIdentity(formatMailboxValue(mailFromRaw));

  const sharedCreatedByTokens = createdByTokens.filter((token) => mailFromTokens.includes(token));
  if (sharedCreatedByTokens.length > 0) {
    return true;
  }

  const recipientItem = item.items.find((detail) => detail.name.toLowerCase() === 'recipient');
  if (!recipientItem) return false;

  const recipientGroups = getRecipientGroups(recipientItem.valueNew || recipientItem.valueOld || recipientItem.description || '');
  const recipientTokens = tokenizeIdentity(
    [...recipientGroups.to, ...recipientGroups.cc, ...recipientGroups.bcc].join(' ')
  );

  return recipientTokens.length > 0 && !recipientTokens.some((token) => mailFromTokens.includes(token));
};

const getRecipientDetailItem = (item: TimelineChange, preferred: 'to' | 'cc' | 'bcc' | 'user') => {
  const recipientItem = item.items.find((detail) => detail.name.toLowerCase() === 'recipient');
  if (!recipientItem) return null;

  const groups = getRecipientGroups(recipientItem.valueNew || recipientItem.valueOld || recipientItem.description || '');
  const value = groups[preferred][0];
  if (!value) return null;

  return {
    id: `${recipientItem.id}-${preferred}`,
    name: preferred === 'to' ? 'mailTo' : preferred.toUpperCase(),
    valueOld: '',
    valueNew: value,
    description: '',
  } as TimelineItem;
};

const getExpandedTimelineItems = (item: TimelineChange) => {
  return item.items.flatMap((detail) => {
    if (detail.name.toLowerCase() !== 'recipient') {
      return [detail];
    }

    const groups = getRecipientGroups(detail.valueNew || detail.valueOld || detail.description || '');
    const expandedRecipients: TimelineItem[] = [];

    groups.to.forEach((value, index) => {
      expandedRecipients.push({
        id: `${detail.id}-to-${index}`,
        name: 'mailTo',
        valueOld: '',
        valueNew: value,
        description: '',
      });
    });

    groups.cc.forEach((value, index) => {
      expandedRecipients.push({
        id: `${detail.id}-cc-${index}`,
        name: 'cc',
        valueOld: '',
        valueNew: value,
        description: '',
      });
    });

    groups.bcc.forEach((value, index) => {
      expandedRecipients.push({
        id: `${detail.id}-bcc-${index}`,
        name: 'bcc',
        valueOld: '',
        valueNew: value,
        description: '',
      });
    });

    groups.user.forEach((value, index) => {
      expandedRecipients.push({
        id: `${detail.id}-user-${index}`,
        name: 'user',
        valueOld: '',
        valueNew: value,
        description: '',
      });
    });

    return expandedRecipients;
  });
};

const hasChangedValuePair = (detail: TimelineItem) => (
  !!String(detail.valueOld || '').trim() && !!String(detail.valueNew || '').trim()
);

export const formatDetailValue = (detail: TimelineItem) => {
  const rawValue = detail.valueOld ? `${detail.valueOld} -> ${detail.valueNew || '-'}` : detail.valueNew || detail.description || '-';
  const normalizedName = detail.name.toLowerCase();
  if (normalizedName === 'recipient') {
    return formatRecipientValue(rawValue);
  }
  if (normalizedName === 'mailfrom' || normalizedName === 'mailto') {
    return formatMailboxValue(rawValue);
  }
  return stripHtml(rawValue);
};

const getPrimaryDetailForCard = (item: TimelineChange, variant: TimelineVariant) => {
  if (variant.key === 'status-update') {
    return getPreferredStatusDetail(item);
  }

  if (variant.key === 'email-sent') {
    const recipientTo = getRecipientDetailItem(item, 'to');
    if (recipientTo) return recipientTo;
  }

  const preferredNames = (() => {
    if (variant.key === 'email-received' || variant.key === 'email-opened' || variant.key === 'email-visited') {
      return ['mailFrom', 'mailTo', 'subject', 'comment', 'teaser', 'name'];
    }
    if (variant.key === 'email-sent') {
      return ['mailTo', 'mailFrom', 'subject', 'comment', 'teaser', 'name'];
    }
    if (variant.key === 'context-created' || variant.key === 'context-updated') {
      return [
        'customStateName',
        'activityTypeName',
        'projectTypeName',
        'folderTypeName',
        'name',
        'comment',
        'subject',
      ];
    }
    return ['comment', 'subject', 'mailFrom', 'mailTo', 'teaser', 'name'];
  })();
  for (const name of preferredNames) {
    const match = item.items.find((detail) => detail.name.toLowerCase() === name.toLowerCase());
    if (match) return match;
  }

  if (variant.key === 'email-opened' || variant.key === 'email-visited') {
    return null;
  }

  const firstUseful = item.items.find((detail) => detail.name && detail.name.toLowerCase() !== 'recipient');
  return firstUseful || null;
};

export const normalizeLegacyColor = (value: string) => {
  if (!value) return '';
  const reversedHex = Number(value).toString(16).split('').reverse();
  const padding = Array(6 - reversedHex.length).fill('0');
  const hex = `#${[...reversedHex, ...padding].join('')}`;
  return hex === '#ffffff' ? '#FBFCF8' : hex;
};

export const getPreferredStatusDetail = (item: TimelineChange) => {
  const statusNameDetail = item.items.find((detail) => detail.name === 'customStateName');
  if (statusNameDetail && (statusNameDetail.valueNew || statusNameDetail.valueOld || statusNameDetail.description)) {
    return statusNameDetail;
  }
  return item.items[0];
};

export const getLeftChipData = (item: TimelineChange, variant: TimelineVariant) => {
  const statusOnlyDetailNames = new Set(['customstate', 'customstatename', 'customstatecolor']);
  const hasStatusOnlyShape =
    variant.key === 'context-updated'
    && item.items.some((detail) => statusOnlyDetailNames.has(String(detail.name || '').toLowerCase()))
    && item.items.every((detail) => statusOnlyDetailNames.has(String(detail.name || '').toLowerCase()));

  if (['email-sent', 'email-received', 'email-opened', 'email-visited'].includes(variant.key)) {
    const subjectDetail = item.items.find((detail) => detail.name.toLowerCase() === 'subject');
    const subjectValue = subjectDetail ? stripHtml(String(subjectDetail.valueNew || subjectDetail.valueOld || subjectDetail.description || '').trim()) : '';
    if (subjectValue) {
      return {
        text: subjectValue,
        tone: 'neutral' as const,
        statusValue: '',
      };
    }
  }

  if (variant.key === 'notice') {
    const subjectDetail = item.items.find((detail) => detail.name.toLowerCase() === 'subject');
    const subjectValue = subjectDetail ? stripHtml(String(subjectDetail.valueNew || subjectDetail.valueOld || subjectDetail.description || '').trim()) : '';
    if (subjectValue) {
      return {
        text: subjectValue,
        tone: 'neutral' as const,
        statusValue: '',
      };
    }
  }

  if (variant.key === 'status-update' && item.status) {
    return {
      text: 'New status',
      tone: 'status' as const,
      statusValue: item.status,
    };
  }

  if (variant.key === 'status-update') {
    const statusNameDetail = item.items.find((detail) => detail.name === 'customStateName');
    if (statusNameDetail && (statusNameDetail.valueNew || statusNameDetail.valueOld || statusNameDetail.description)) {
      return {
        text: 'New status',
        tone: 'status' as const,
        statusValue: String(statusNameDetail.valueNew || statusNameDetail.valueOld || statusNameDetail.description),
      };
    }
  }

  if (hasStatusOnlyShape) {
    const statusNameDetail = item.items.find((detail) => detail.name === 'customStateName');
    const statusValue = statusNameDetail
      ? String(statusNameDetail.valueNew || statusNameDetail.valueOld || statusNameDetail.description || '').trim()
      : '';
    if (statusValue) {
      return {
        text: 'New status',
        tone: 'status' as const,
        statusValue,
      };
    }
  }

  const firstDetail = getPrimaryDetailForCard(item, variant);
  if (!firstDetail) return null;

  const label = formatFieldName(firstDetail.name);
  const value = ['form-update', 'record-change', 'context-updated'].includes(variant.key) && hasChangedValuePair(firstDetail)
    ? stripHtml(String(firstDetail.valueNew || '').trim())
    : formatDetailValue(firstDetail);
  if (!value) {
    return {
      text: label,
      tone: variant.chipTone || 'neutral',
      statusValue: '',
    };
  }

  if (variant.chipTone === 'status') {
    return {
      text: label,
      tone: 'status' as const,
      statusValue: value,
    };
  }

  if (label === 'Subject') {
    return {
      text: value,
      tone: 'neutral' as const,
      statusValue: '',
    };
  }

  return {
    text: `${label}: ${value}`,
    tone: 'neutral' as const,
    statusValue: '',
  };
};

export const getRightChipText = (item: TimelineChange, variant: TimelineVariant) => {
  const haystack = `${item.title} ${item.description} ${item.changeType}`.toLowerCase();
  const firstDetail = item.items[0];
  const visitValue = firstDetail?.valueNew || firstDetail?.valueOld || '1';
  const recipientItem = item.items.find((detail) => detail.name.toLowerCase() === 'recipient');
  const recipientGroups = recipientItem ? getRecipientGroups(recipientItem.valueNew || recipientItem.valueOld || recipientItem.description || '') : null;
  const ccCount = recipientGroups?.cc.length || 0;

  if (variant.key === 'email-opened') return `Opened ${visitValue}x`;
  if (variant.key === 'email-visited') return `URL visited ${visitValue}x`;
  if (variant.key === 'email-sent' && ccCount > 0) return `Cc ${ccCount}`;
  return haystack.includes('opened') ? 'Opened 1x' : '';
};

export const getContextTrail = (
  item: TimelineChange,
  t: any,
  fallbackKey: string,
  fallbackDefault: string,
  currentContextId?: string
) => {
  const contextNodes = Array.isArray(item.contextPath) ? [...item.contextPath] : [];
  if (contextNodes.length > 0) {
    const orderedNodes = [...contextNodes].sort((left, right) => Number(right.level || 0) - Number(left.level || 0));
    const relativeNodes = currentContextId
      ? (() => {
          const currentIndex = orderedNodes.findIndex((node) => String(node.id) === String(currentContextId));
          return currentIndex >= 0 ? orderedNodes.slice(currentIndex + 1) : orderedNodes;
        })()
      : orderedNodes;

    const segments = relativeNodes
      .map((node, index) => {
        const baseLabel = String(node.name || node.typeName || '').trim();
        if (!baseLabel) return '';
        const isLast = index === relativeNodes.length - 1;
        const refSuffix = isLast && node.refId ? ` (${node.refId})` : '';
        return `${baseLabel}${refSuffix}`;
      })
      .filter(Boolean);

    if (segments.length > 0) {
      return {
        left: segments.slice(0, -1).join(' > '),
        right: segments[segments.length - 1],
        targetId: String(relativeNodes[relativeNodes.length - 1]?.id || ''),
      };
    }

    return { left: '', right: '', targetId: '' };
  }

  const firstDetail = item.items.find((detail) => detail.name && detail.name.toLowerCase() !== 'recipient') || item.items[0];
  const left = '';
  const right =
    firstDetail?.description
    || (firstDetail ? formatFieldName(firstDetail.name) : '')
    || item.changeType
    || t(fallbackKey, { ns: 'layout_components', defaultValue: fallbackDefault });
  return { left, right, targetId: '' };
};

export const getDisplayTitle = (item: TimelineChange, variant: TimelineVariant) => {
  const statusOnlyDetailNames = new Set(['customstate', 'customstatename', 'customstatecolor']);
  const hasStatusOnlyShape =
    variant.key === 'context-updated'
    && item.items.some((detail) => statusOnlyDetailNames.has(String(detail.name || '').toLowerCase()))
    && item.items.every((detail) => statusOnlyDetailNames.has(String(detail.name || '').toLowerCase()));
  const isGenericDescription = (value: string) => {
    const normalized = String(value || '').trim().toLowerCase();
    return [
      'new message created',
      'details changed',
      'changed',
      'created',
      'updated',
    ].includes(normalized);
  };
  const getDetailByName = (name: string) =>
    item.items.find((detail) => String(detail.name || '').toLowerCase() === name.toLowerCase());
  const subjectValue = (() => {
    const subjectDetail = getDetailByName('subject');
    return subjectDetail ? formatDetailValue(subjectDetail) : '';
  })();
  const commentValue = (() => {
    const commentDetail = getDetailByName('comment');
    return commentDetail ? formatDetailValue(commentDetail) : '';
  })();
  const firstDetail = (() => {
    if (variant.key === 'email-received' || variant.key === 'email-opened' || variant.key === 'email-visited') {
      return getMailFromDetailItem(item) || getPrimaryDetailForCard(item, variant);
    }
    if (variant.key === 'email-sent') {
      return getRecipientDetailItem(item, 'to') || getPrimaryDetailForCard(item, variant);
    }
    return getPrimaryDetailForCard(item, variant);
  })();
  const detailValue = firstDetail ? formatDetailValue(firstDetail) : '';
  const description = String(item.description || item.title || '').trim();
  const visitCountDetail = item.items.find((detail) => detail.name.toLowerCase() === 'numberofvisits');
  const visitCount = visitCountDetail ? formatDetailValue(visitCountDetail) : '';
  const preferredNonGenericTitle = subjectValue || detailValue || commentValue;

  if (variant.key === 'email-sent') return detailValue ? `Sent to: ${detailValue}` : description || 'Sent message';
  if (variant.key === 'email-received') return detailValue ? `From: ${detailValue}` : description || 'Received message';
  if (variant.key === 'email-opened') {
    if (detailValue) return `From: ${detailValue}`;
    if (visitCount) return `Opened ${visitCount}x`;
    return description || 'Mail open';
  }
  if (variant.key === 'email-visited') {
    if (detailValue) return `Recipient: ${detailValue}`;
    if (visitCount) return `URL visited ${visitCount}x`;
    return description || 'URL visited';
  }
  if (variant.key === 'phone-call') {
    if (preferredNonGenericTitle) return preferredNonGenericTitle;
    return isGenericDescription(description) ? 'Phone call' : description || item.title || 'Phone call';
  }
  if (variant.key === 'notice') {
    const senderLabel = String(item.createdByName || '').trim()
      || (() => {
        const mailFromDetail = getMailFromDetailItem(item);
        return mailFromDetail ? formatDetailValue(mailFromDetail) : '';
      })();
    if (senderLabel) return `From: ${senderLabel}`;
    if (preferredNonGenericTitle) return preferredNonGenericTitle;
    return isGenericDescription(description) ? 'Message' : description || item.title || 'Message';
  }
  if (hasStatusOnlyShape) {
    return 'Status changed';
  }
  if (isGenericDescription(description) && preferredNonGenericTitle) {
    return preferredNonGenericTitle;
  }
  return description || item.title || 'Timeline entry';
};

const getDetailNames = (item: TimelineChange) =>
  item.items.map((entry) => String(entry.name || '').toLowerCase());

const hasAnyDetailName = (item: TimelineChange, names: string[]) => {
  const detailNames = getDetailNames(item);
  return names.some((name) => detailNames.includes(name.toLowerCase()));
};

const isEmailLikeEntry = (item: TimelineChange, haystack: string, objectName: string) => {
  const mailObjectNames = ['email', 'mail', 'mail-open', 'url-visit', 'mail-url-visit'];
  const mailFieldNames = ['mailfrom', 'mailto', 'subject', 'teaser', 'recipient', 'cc', 'bcc'];
  const recipientGroups = getPrimaryRecipientDetail(item);
  const hasInternalUserOnlyRecipients = !!recipientGroups
    && recipientGroups.user.length > 0
    && recipientGroups.to.length === 0
    && recipientGroups.cc.length === 0
    && recipientGroups.bcc.length === 0;

  if ((objectName === 'notice' || objectName === 'message') && !hasStrongEmailEvidence(item)) {
    return false;
  }

  if ((objectName === 'issue' || objectName === 'task') && hasInternalUserOnlyRecipients && !getMailFromValue(item).trim()) {
    return false;
  }

  if (mailObjectNames.includes(objectName)) return true;
  if (hasAnyDetailName(item, mailFieldNames)) return true;

  return (
    haystack.includes('mail ') ||
    haystack.includes(' mail') ||
    haystack.includes('email ') ||
    haystack.includes(' email')
  );
};

export const getTimelineVariant = (item: TimelineChange): TimelineVariant => {
  const haystack = `${item.title} ${item.description} ${item.changeType} ${item.items.map((entry) => `${entry.name} ${entry.valueNew}`).join(' ')}`.toLowerCase();
  const objectName = String(item.objectName || '').toLowerCase();
  const normalizedChangeType = String(item.changeType || '').toLowerCase();
  const isContextEntry = ['folder', 'project', 'activity'].includes(objectName);
  const recipientGroups = getPrimaryRecipientDetail(item);
  const hasMailFrom = !!getMailFromValue(item).trim();
  const hasInternalUserOnlyRecipients = !!recipientGroups
    && recipientGroups.user.length > 0
    && recipientGroups.to.length === 0
    && recipientGroups.cc.length === 0
    && recipientGroups.bcc.length === 0;
  const hasSubjectAndComment =
    item.items.some((detail) => String(detail.name || '').toLowerCase() === 'subject' && !!formatDetailValue(detail).trim())
    && item.items.some((detail) => String(detail.name || '').toLowerCase() === 'comment' && !!formatDetailValue(detail).trim());
  const hasInternalMessageShape =
    !hasMailFrom
    && (
      (
        ['issue', 'task', 'notice', 'message'].includes(objectName)
        && hasInternalUserOnlyRecipients
        && hasSubjectAndComment
      )
      || (
        haystack.includes('new message created')
        && (
          !recipientGroups
          || (
            recipientGroups.to.length === 0
            && recipientGroups.cc.length === 0
            && recipientGroups.bcc.length === 0
          )
        )
      )
    );
  const isNoticeEntry =
    ((objectName === 'notice' || objectName === 'message') && !isEmailLikeEntry(item, haystack, objectName))
    || hasInternalMessageShape;
  const hasCustomStateChange = item.items.some((entry) => entry.name === 'customState' || entry.name === 'customStateName');
  const emailLikeEntry = isEmailLikeEntry(item, haystack, objectName);
  const isPhoneCallEntry =
    objectName === 'phonecall'
    || haystack.includes('phone call')
    || haystack.includes('phonecall');
  const likelySentEmail = emailLikeEntry && isLikelySentEmail(item);
  const hasVisitCountDetail = item.items.some((entry) => String(entry.name || '').toLowerCase() === 'numberofvisits');
  const hasExplicitUrlField = item.items.some((entry) => {
    const fieldName = String(entry.name || '').toLowerCase();
    return ['url', 'link', 'mailurl', 'mailUrl', 'visitedurl'].some((candidate) => fieldName.includes(candidate.toLowerCase()));
  });
  const looksLikeMailOpen =
    objectName === 'mail-open' ||
    hasVisitCountDetail ||
    haystack.includes('mail open') ||
    haystack.includes('mail-open');
  const looksLikeUrlVisit =
    objectName === 'url-visit' ||
    objectName === 'mail-url-visit' ||
    (hasExplicitUrlField && hasVisitCountDetail) ||
    hasExplicitUrlField ||
    haystack.includes('mail-url-visit') ||
    haystack.includes('url-visit') ||
    haystack.includes('url visit') ||
    haystack.includes('visited url');

  if (isPhoneCallEntry) {
    return {
      key: 'phone-call',
      icon: <Telephone size={18} />,
      iconClassName: 'icon_generic',
      chipTone: 'neutral',
      supportsExpand: true,
    };
  }
  if (looksLikeUrlVisit && emailLikeEntry) {
    return {
      key: 'email-visited',
      icon: <Envelope size={18} />,
      iconClassName: 'icon_mail',
      badgeIcon: <Link45deg size={9} />,
      badgeClassName: 'badge_info',
      chipTone: 'neutral',
      supportsExpand: false,
    };
  }
  if (emailLikeEntry && looksLikeMailOpen) {
    return {
      key: 'email-opened',
      icon: <EnvelopeOpen size={18} />,
      iconClassName: 'icon_mail_opened',
      badgeIcon: <EnvelopeOpen size={9} />,
      badgeClassName: 'badge_info',
      chipTone: 'neutral',
      supportsExpand: false,
    };
  }
  if (emailLikeEntry && (haystack.includes('sent') || haystack.includes('outgoing') || likelySentEmail)) {
    return {
      key: 'email-sent',
      icon: <EnvelopeArrowUp size={18} />,
      iconClassName: 'icon_sent',
      accentClassName: 'accent_success',
      chipTone: 'neutral',
      supportsExpand: true,
    };
  }
  if (emailLikeEntry) {
    return {
      key: 'email-received',
      icon: <Envelope size={18} />,
      iconClassName: 'icon_mail',
      chipTone: 'neutral',
      supportsExpand: true,
    };
  }
  if (isContextEntry && normalizedChangeType === 'created') {
    return {
      key: 'context-created',
      icon: <Building size={18} />,
      iconClassName: 'icon_corporate',
      badgeIcon: <Plus size={9} />,
      badgeClassName: 'badge_corporate',
      chipTone: 'status',
      supportsExpand: false,
    };
  }
  if (isContextEntry && normalizedChangeType === 'updated') {
    return {
      key: 'context-updated',
      icon: <Building size={18} />,
      iconClassName: 'icon_corporate',
      showReloadBadge: true,
      accentClassName: 'accent_warning',
      chipTone: hasCustomStateChange ? 'status' : 'neutral',
      supportsExpand: true,
    };
  }
  if (isNoticeEntry) {
    return {
      key: 'notice',
      icon: <Bell size={18} />,
      iconClassName: 'icon_corporate',
      chipTone: 'neutral',
      supportsExpand: true,
    };
  }
  if (haystack.includes('form')) {
    return {
      key: 'form-update',
      icon: <FileEarmarkText size={18} />,
      iconClassName: 'icon_form',
      showReloadBadge: true,
      accentClassName: 'accent_warning',
      chipTone: 'neutral',
      supportsExpand: true,
    };
  }
  if (haystack.includes('status') || hasCustomStateChange) {
    return {
      key: 'status-update',
      icon: <Building size={18} />,
      iconClassName: 'icon_corporate',
      showReloadBadge: true,
      accentClassName: 'accent_warning',
      chipTone: 'status',
      supportsExpand: false,
    };
  }
  if (haystack.includes('goal') || haystack.includes('achieved')) {
    return {
      key: 'goal-achieved',
      icon: <Check2Circle size={18} />,
      iconClassName: 'icon_goal',
      chipTone: 'neutral',
      supportsExpand: false,
    };
  }
  if (normalizedChangeType === 'updated' || normalizedChangeType === 'created') {
    return {
      key: 'record-change',
      icon: <FileEarmarkText size={18} />,
      iconClassName: 'icon_record',
      chipTone: 'neutral',
      supportsExpand: true,
    };
  }
  if (haystack.includes('unsubscribe') || haystack.includes('cancel')) {
    return {
      key: 'unsubscribe',
      icon: <PersonX size={18} />,
      iconClassName: 'icon_danger',
      chipTone: 'neutral',
      supportsExpand: false,
    };
  }
  return {
    key: 'generic',
    icon: <Bell size={18} />,
    iconClassName: 'icon_generic',
    chipTone: 'neutral',
    supportsExpand: true,
  };
};

export const getVisibleDetailItems = (item: TimelineChange, variant: TimelineVariant) => {
  const hiddenTechnicalDetailNames = new Set([
    'customstate',
    'customstatecolor',
    'activitytype',
    'projecttype',
    'foldertype',
  ]);
  const hasReadableCustomStateName = item.items.some(
    (detail) => detail.name === 'customStateName' && !!String(detail.valueNew || detail.valueOld || detail.description || '').trim()
  );
  const hasReadableActivityTypeName = item.items.some(
    (detail) => detail.name === 'activityTypeName' && !!String(detail.valueNew || detail.valueOld || detail.description || '').trim()
  );
  const hasReadableProjectTypeName = item.items.some(
    (detail) => detail.name === 'projectTypeName' && !!String(detail.valueNew || detail.valueOld || detail.description || '').trim()
  );
  const hasReadableFolderTypeName = item.items.some(
    (detail) => detail.name === 'folderTypeName' && !!String(detail.valueNew || detail.valueOld || detail.description || '').trim()
  );
  const expandedItems = getExpandedTimelineItems(item).flatMap((detail) => {
    const rawDetailName = String(detail.name || '').toLowerCase();

    if (hiddenTechnicalDetailNames.has(rawDetailName)) return [];

    if (rawDetailName === 'customstate' && hasReadableCustomStateName) return [];
    if (rawDetailName === 'customstatecolor' && hasReadableCustomStateName) return [];
    if (rawDetailName === 'activitytype' && hasReadableActivityTypeName) return [];
    if (rawDetailName === 'projecttype' && hasReadableProjectTypeName) return [];
    if (rawDetailName === 'foldertype' && hasReadableFolderTypeName) return [];

    if (['form-update', 'record-change', 'context-updated'].includes(variant.key) && hasChangedValuePair(detail)) {
      return [
        {
          id: `${detail.id}-from`,
          name: 'From',
          valueOld: '',
          valueNew: String(detail.valueOld || ''),
          description: '',
        },
        {
          id: `${detail.id}-to`,
          name: 'To',
          valueOld: '',
          valueNew: String(detail.valueNew || ''),
          description: '',
        },
      ];
    }

    return [detail];
  });
  const lowerComment = expandedItems.find((detail) => detail.name.toLowerCase() === 'comment');
  const lowerSubject = expandedItems.find((detail) => detail.name.toLowerCase() === 'subject');
  const descriptionText = String(item.description || item.title || '').trim().toLowerCase();
  const leftChip = getLeftChipData(item, variant);
  const emailDetailNames = new Set(['mailfrom', 'mailto', 'subject', 'comment', 'teaser', 'recipient', 'cc', 'bcc', 'user']);
  const statusDetailNames = new Set([
    'customstate',
    'customstatename',
    'customstatecolor',
    'projecttype',
    'projecttypename',
    'activitytype',
    'activitytypename',
    'foldertype',
    'foldertypename',
    'name',
  ]);
  const hasNonStatusDetails = expandedItems.some((detail) => {
    const detailName = String(detail.name || '').toLowerCase();
    if (!detailName || detailName === 'recipient') return false;
    if (statusDetailNames.has(detailName)) return false;
    return !!formatDetailValue(detail).trim();
  });
  const preserveChangedFieldDetails =
    (variant.key === 'context-updated' || variant.key === 'record-change')
    && expandedItems.some((detail) => !!String(detail.valueOld || '').trim() || !!String(detail.valueNew || '').trim());
  const priorityByVariant: Record<string, string[]> = {
    'email-sent': ['mailfrom', 'mailto', 'cc', 'bcc', 'subject', 'comment', 'teaser'],
    'email-received': ['mailfrom', 'mailto', 'cc', 'bcc', 'subject', 'comment', 'teaser'],
    'email-opened': ['mailfrom', 'mailto', 'cc', 'bcc', 'subject', 'comment', 'teaser'],
    'email-visited': ['mailfrom', 'mailto', 'cc', 'bcc', 'subject', 'comment', 'teaser'],
    'status-update': ['customstatename', 'customstatecolor', 'name', 'projecttypename', 'projecttype', 'activitytypename', 'activitytype', 'foldertypename', 'foldertype'],
    'form-update': ['name', 'comment'],
  };
  const priorities = priorityByVariant[variant.key];
  const getPriority = (detailName: string) => {
    const index = priorities?.indexOf(detailName.toLowerCase()) ?? -1;
    return index === -1 ? (priorities?.length ?? 0) : index;
  };

  if (['email-sent', 'email-received', 'email-opened', 'email-visited'].includes(variant.key)) {
    const emailOnlyItems = expandedItems.filter((detail) => {
      const detailName = String(detail.name || '').toLowerCase();
      const detailValue = formatDetailValue(detail).trim();
      if (!detail.name || !detailValue || detailValue === '-') return false;
      if (!emailDetailNames.has(detailName)) return false;

      if (
        lowerComment &&
        lowerSubject &&
        detailName === 'subject' &&
        String(lowerComment.valueNew || lowerComment.valueOld || lowerComment.description || '').trim() ===
          String(lowerSubject.valueNew || lowerSubject.valueOld || lowerSubject.description || '').trim()
      ) {
        return false;
      }

      return true;
    });

    return [...emailOnlyItems].sort((left, right) => {
      const leftPriority = getPriority(left.name || '');
      const rightPriority = getPriority(right.name || '');

      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      return String(left.name || '').localeCompare(String(right.name || ''));
    });
  }

  const filteredItems = expandedItems.filter((detail) => {
    const detailName = detail.name.toLowerCase();
    const detailValue = formatDetailValue(detail).trim();
    if (!detail.name) return false;
    if (detailName === 'recipient') return false;
    if (hiddenTechnicalDetailNames.has(detailName)) return false;
    if (!detailValue || detailValue === '-') return false;
    if (detailName === 'customstate' && hasReadableCustomStateName) return false;
    if (detailName === 'customstatecolor' && hasReadableCustomStateName) return false;
    if (detailName === 'activitytype' && hasReadableActivityTypeName) return false;
    if (detailName === 'projecttype' && hasReadableProjectTypeName) return false;
    if (detailName === 'foldertype' && hasReadableFolderTypeName) return false;

    if (leftChip && !preserveChangedFieldDetails && variant.key !== 'notice') {
      const detailLabel = formatFieldName(detail.name);
      if (leftChip.tone === 'status') {
        if (detailLabel === leftChip.text && detailValue === leftChip.statusValue) return false;
      } else if (detailLabel && `${detailLabel}: ${detailValue}` === leftChip.text) {
        return false;
      }
    }

    if (detailName === 'comment' && detailValue && descriptionText.includes(detailValue.toLowerCase())) {
      return false;
    }

    if (
      lowerComment &&
      lowerSubject &&
      detailName === 'subject' &&
      String(lowerComment.valueNew || lowerComment.valueOld || lowerComment.description || '').trim() ===
        String(lowerSubject.valueNew || lowerSubject.valueOld || lowerSubject.description || '').trim()
    ) {
      return false;
    }

    if (variant.key === 'status-update' && !statusDetailNames.has(detailName)) {
      return false;
    }

    if (variant.key === 'context-created' && !statusDetailNames.has(detailName)) {
      return false;
    }

    if (variant.key === 'context-updated' && !hasNonStatusDetails && !statusDetailNames.has(detailName)) {
      return false;
    }

    return true;
  });

  if (!priorities) return filteredItems;

  const sortedItems = [...filteredItems].sort((left, right) => {
    const leftPriority = getPriority(left.name || '');
    const rightPriority = getPriority(right.name || '');

    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }

    return String(left.name || '').localeCompare(String(right.name || ''));
  });

  return sortedItems;
};

export const canExpandTimelineItem = (
  item: TimelineChange,
  variant: TimelineVariant,
  visibleDetailItems: TimelineItem[]
) => {
  if (visibleDetailItems.length === 0) return false;
  if (variant.supportsExpand) return true;

  const normalizedChangeType = String(item.changeType || '').toLowerCase();
  const hasOldAndNewValues = item.items.some(
    (detail) => !!String(detail.valueOld || '').trim() && !!String(detail.valueNew || '').trim()
  );
  const hasMultipleVisibleDetails = visibleDetailItems.length > 1;

  return ['created', 'updated'].includes(normalizedChangeType) && (hasOldAndNewValues || hasMultipleVisibleDetails);
};
